import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type PaginationState,
  type RowData,
} from '@tanstack/react-table';
import { Icon } from '../primitives/Icon';
import { Skeleton } from '../primitives/Skeleton';
import { EmptyState } from '../layout/EmptyState';

/**
 * DataTable — thin Tailwind-styled wrapper around TanStack Table.
 * Supports sort, filter, pagination, row click, and custom cell renderers.
 *
 * Backward compatibility: the old ad-hoc `Column<T>` shape (header + cell + align + width)
 * is still accepted and adapted to TanStack internally, so existing pages keep working.
 */

// Legacy column shape kept for backward compatibility
export interface Column<T> {
  header: string;
  cell: (row: T) => React.ReactNode;
  key?: string;
  /** Accessor key for sorting. If omitted, column is unsorted. */
  sortKey?: keyof T | string;
  align?: 'start' | 'center' | 'end';
  width?: string;
  enableSorting?: boolean;
  /** Hide this column in mobile card view */
  hideOnMobile?: boolean;
  /** Treat as primary column (used as title in mobile card view) */
  primary?: boolean;
}

interface DataTableProps<T extends RowData> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  rowKey?: (row: T) => string;
  onRowClick?: (row: T) => void;
  /**
   * Where a row leads. With it, Ctrl/Cmd/Shift-click and middle-click open
   * the row in a new tab, the way a link does; a plain click navigates
   * (or calls onRowClick when given).
   */
  rowHref?: (row: T) => string;
  empty?: { title: string; description?: string; icon?: string; action?: React.ReactNode };
  /** Global search string (filters across all columns). */
  searchValue?: string;
  /** Page size for pagination. Set to 0 to disable pagination. */
  pageSize?: number;
  className?: string;
  /** Sticky header while scrolling. */
  stickyHeader?: boolean;
}

export function DataTable<T extends RowData>({
  data,
  columns: userColumns,
  loading,
  rowKey,
  onRowClick,
  rowHref,
  empty,
  searchValue = '',
  pageSize = 25,
  className = '',
  stickyHeader = false,
}: DataTableProps<T>) {
  const navigate = useNavigate();
  const clickable = !!(onRowClick || rowHref);
  // A click on a link, button or field inside the row is that control's own;
  // without this, a name rendered as a <Link> would navigate twice.
  const fromControl = (e: React.MouseEvent) =>
    !!(e.target as HTMLElement).closest('a,button,input,select,textarea,label,[role="button"]');
  const onRow = (row: T, e: React.MouseEvent) => {
    if (fromControl(e)) return;
    const href = rowHref?.(row);
    if (href && (e.metaKey || e.ctrlKey || e.shiftKey)) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (onRowClick) onRowClick(row);
    else if (href) navigate(href);
  };
  const onRowAux = (row: T, e: React.MouseEvent) => {
    const href = rowHref?.(row);
    if (e.button !== 1 || !href || fromControl(e)) return;
    e.preventDefault();
    window.open(href, '_blank', 'noopener,noreferrer');
  };
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: pageSize || 9999 });

  // Adapt legacy Column<T> to TanStack ColumnDef
  const columns = useMemo<ColumnDef<T>[]>(() =>
    userColumns.map((c, i) => ({
      id: c.key || String(i),
      header: c.header,
      accessorFn: c.sortKey ? (row: T) => (row as any)[c.sortKey!] : undefined,
      cell: (ctx) => c.cell(ctx.row.original),
      enableSorting: c.enableSorting ?? !!c.sortKey,
      meta: { align: c.align, width: c.width },
    })),
  [userColumns]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters, pagination, globalFilter: searchValue },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: pageSize ? getPaginationRowModel() : undefined,
    globalFilterFn: (row, _columnId, filterValue) => {
      if (!filterValue) return true;
      const q = String(filterValue).toLowerCase();
      // Search across all accessor values AND the original row's string fields
      const original = row.original as any;
      return Object.values(original).some((v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' || typeof v === 'number') {
          return String(v).toLowerCase().includes(q);
        }
        if (typeof v === 'object' && v !== null) {
          return JSON.stringify(v).toLowerCase().includes(q);
        }
        return false;
      });
    },
    getRowId: rowKey as any,
    manualPagination: false,
  });

  // Initial load (no data yet): show skeleton
  if (loading && (!data || data.length === 0)) {
    return (
      <div className={`rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden ${className}`}>
        <div className="p-5 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={42} />)}
        </div>
      </div>
    );
  }

  if (!loading && (!data || data.length === 0)) {
    return (
      <div className={`rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden ${className}`}>
        <EmptyState
          icon={empty?.icon || 'inbox'}
          title={empty?.title || 'אין נתונים להצגה'}
          description={empty?.description}
          action={empty?.action}
        />
      </div>
    );
  }

  const alignClass = (align?: 'start' | 'center' | 'end') =>
    align === 'end' ? 'text-end' : align === 'center' ? 'text-center' : 'text-start';

  const rows = table.getRowModel().rows;
  const totalRows = table.getFilteredRowModel().rows.length;
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();
  const canPaginate = pageSize > 0 && totalRows > pageSize;

  // Mobile card view — rendered on screens < md
  const mobileCards = (
    <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
      {rows.length === 0 ? (
        <div className="py-12">
          <EmptyState icon="search_off" title="לא נמצאו תוצאות" description="נסה לשנות את מונחי החיפוש" />
        </div>
      ) : (
        rows.map(row => {
          const original = row.original as any;
          const primaryColumn = userColumns.find(c => c.primary) || userColumns[0];
          const secondaryColumns = userColumns.filter(c => c !== primaryColumn && !c.hideOnMobile);
          return (
            <div
              key={row.id}
              onClick={clickable ? (e) => onRow(original, e) : undefined}
              onAuxClick={rowHref ? (e) => onRowAux(original, e) : undefined}
              className={[
                'p-4 transition-colors',
                clickable ? 'active:bg-slate-50 dark:active:bg-slate-800/50 cursor-pointer' : '',
              ].join(' ')}
            >
              <div className="mb-2">
                {primaryColumn.cell(original)}
              </div>
              {secondaryColumns.length > 0 && (
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                  {secondaryColumns.map((col, i) => (
                    <div key={col.key || i} className="min-w-0">
                      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">
                        {col.header}
                      </div>
                      <div className="text-xs min-w-0 truncate">{col.cell(original)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div className={`relative rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden ${className}`}>
      {/* Subtle loading overlay when refetching with existing data */}
      {loading && data && data.length > 0 && (
        <div className="absolute top-0 end-0 m-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm border border-slate-200 shadow-sm">
          <Icon name="progress_activity" size={14} className="animate-spin text-primary" />
          <span className="text-[11px] font-semibold text-slate-600">מעדכן...</span>
        </div>
      )}

      {/* Mobile: card list */}
      {mobileCards}

      {/* Desktop: table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className={`bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta as any;
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      style={meta?.width ? { width: meta.width } : undefined}
                      className={[
                        'px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400',
                        alignClass(meta?.align),
                        canSort ? 'cursor-pointer select-none hover:text-slate-900 dark:hover:text-white transition-colors' : '',
                      ].join(' ')}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <Icon
                            name={sorted === 'asc' ? 'arrow_upward' : sorted === 'desc' ? 'arrow_downward' : 'unfold_more'}
                            size={12}
                            className={sorted ? 'text-primary' : 'text-slate-400'}
                          />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12">
                  <EmptyState
                    icon="search_off"
                    title="לא נמצאו תוצאות"
                    description="נסה לשנות את מונחי החיפוש"
                  />
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr
                  key={row.id}
                  onClick={clickable ? (e) => onRow(row.original, e) : undefined}
                  onAuxClick={rowHref ? (e) => onRowAux(row.original, e) : undefined}
                  className={[
                    'transition-colors',
                    clickable ? 'hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer' : '',
                  ].join(' ')}
                >
                  {row.getVisibleCells().map(cell => {
                    const meta = cell.column.columnDef.meta as any;
                    return (
                      <td key={cell.id} className={`px-5 py-3 text-sm ${alignClass(meta?.align)}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {canPaginate && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
          <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
            מציג {rows.length} מתוך {totalRows} תוצאות
          </div>
          {/* pe-16 keeps the "next" arrow clear of the floating dialer FAB,
              which docks bottom-end (size-14 at end-4, "end" = left in RTL) —
              the same side the pagination sits on. Applied at every width
              because the FAB shows on mobile too. */}
          <div className="flex items-center gap-1 pe-16">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="size-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center transition-colors"
              aria-label="עמוד קודם"
            >
              <Icon name="chevron_right" size={16} />
            </button>
            <span className="text-xs font-semibold tabular-nums px-2">
              עמוד {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="size-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center transition-colors"
              aria-label="עמוד הבא"
            >
              <Icon name="chevron_left" size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
